import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthManagerService } from 'src/app/services/auth-manager/auth-manager.service';
import { CheckoutService } from 'src/app/services/checkout/checkout.service';
import { LogService } from 'src/app/services/common/log.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { UserService } from 'src/app/services/user/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-identi-smart-tag',
  templateUrl: './identi-smart-tag.component.html',
  styleUrl: './identi-smart-tag.component.scss'
})
export class IdentiSmartTagComponent implements OnInit, AfterViewInit, OnDestroy {
  public canvasWidth = 0;
  public canvasHeight = 0;
  public connectorPaths: string[] = [];

  // True (buy) until a signed-in user turns out to already have the plan (then the CTA
  // takes them to /get-identi-smart-tag instead of starting checkout).
  public smartTagSubAvailable = true;

  @ViewChild('stepsCanvas') private stepsCanvas?: ElementRef<HTMLElement>;
  @ViewChildren('stepAnchor') private stepAnchors?: QueryList<ElementRef<HTMLElement>>;

  private readonly isBrowser: boolean;
  private resizeObserver?: ResizeObserver;

  constructor(
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    @Inject(PLATFORM_ID) platformId: Object,
    private router: Router,
    private authManager: AuthManagerService,
    private checkoutService: CheckoutService,
    private logService: LogService,
    private membershipOptionsService: MemberShipOptionsService,
    private userService: UserService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.authManager.isAuthenticated) return;

    this.userService.getMe().subscribe((user) => {
      this.smartTagSubAvailable = this.membershipOptionsService.smartTagSubAvailable(user);
    });
  }

  // Signed-out visitors are sent to log in first, then back to this page.
  // A user who already has the sub goes to the product page
  // instead of re-buying; everyone else starts checkout.
  public handleSmartTagCta(): void {
    this.router.navigate(['/choose-plan'], { queryParams: { plan: 'locator' } });
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || !this.stepsCanvas) return;

    // Outside the zone so scroll/resize churn doesn't trigger change detection
    // on every frame; drawConnectors re-enters explicitly when it has new paths.
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.drawConnectors());
      this.resizeObserver.observe(this.stepsCanvas!.nativeElement);
    });

    this.drawConnectors();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  public onStepImageLoad(): void {
    this.drawConnectors();
  }

  private drawConnectors(): void {
    const canvas = this.stepsCanvas?.nativeElement;
    const anchors = this.stepAnchors?.toArray() ?? [];
    if (!canvas || anchors.length < 2) return;

    const box = canvas.getBoundingClientRect();
    const points = anchors.map((anchor) => {
      const rect = anchor.nativeElement.getBoundingClientRect();
      return {
        x: rect.left - box.left + rect.width / 2,
        y: rect.top - box.top + rect.height / 2,
      };
    });

    // Anchors come in pairs: leave step N, enter step N+1.
    const paths: string[] = [];
    for (let i = 0; i + 1 < points.length; i += 2) {
      const from = points[i];
      const to = points[i + 1];
      // Control points sit directly below the start and above the end, so the
      // curve leaves and arrives vertically and does its horizontal travel in
      // between.
      const CURVE_DROP = 0.8;
      const drop = Math.max(Math.abs(to.y - from.y) * CURVE_DROP, 120);
      paths.push(
        `M ${from.x} ${from.y} C ${from.x} ${from.y + drop}, ${to.x} ${to.y - drop}, ${to.x} ${to.y}`
      );
    }

    this.zone.run(() => {
      this.canvasWidth = box.width;
      this.canvasHeight = box.height;
      this.connectorPaths = paths;
      this.cdr.markForCheck();
    });
  }
}
